// ============================================================================
// Roles & Permissions — Mock Data
// Used until the backend RP-BE endpoints are implemented.
// ============================================================================

import type { Role, RoleUser } from '@/types/roles.types';

// ── Default permissions helpers ───────────────────────────────────────────────

const ALL_PROJECT_TABS_EDIT = {
  overview: 'Edit',
  financials: 'Edit',
  billing: 'Edit',
  salesBudget: 'Edit',
  costBudget: 'Edit',
  resourceBudget: 'Edit',
  costCentres: 'Edit',
  purchases: 'Edit',
  time: 'Edit',
  expenses: 'Edit',
  team: 'Edit',
  resourcing: 'Edit',
  milestones: 'Edit',
  changeRequests: 'Edit',
  audit: 'Edit',
  reports: 'Edit',
  forecast: 'Edit',
} as const;

const PM_PROJECT_TABS = {
  overview: 'Edit',
  financials: 'Edit',
  billing: 'Edit',
  salesBudget: 'Edit',
  costBudget: 'Edit',
  resourceBudget: 'Edit',
  costCentres: 'Edit',
  purchases: 'View',
  time: 'View',
  expenses: 'View',
  team: 'Edit',
  resourcing: 'View',
  milestones: 'Edit',
  changeRequests: 'Edit',
  audit: 'View',
  reports: 'View',
  forecast: 'Edit',
} as const;

const EXECUTIVE_PROJECT_TABS = {
  overview: 'View',
  financials: 'View',
  billing: 'View',
  salesBudget: 'View',
  costBudget: 'View',
  resourceBudget: 'View',
  costCentres: 'View',
  purchases: 'View',
  time: 'View',
  expenses: 'View',
  team: 'View',
  resourcing: 'View',
  milestones: 'View',
  changeRequests: 'View',
  audit: 'Hidden',
  reports: 'View',
  forecast: 'View',
} as const;

const PROJECT_TEAM_PROJECT_TABS = {
  overview: 'View',
  financials: 'Hidden',
  billing: 'Hidden',
  salesBudget: 'Hidden',
  costBudget: 'Hidden',
  resourceBudget: 'Hidden',
  costCentres: 'Hidden',
  purchases: 'Hidden',
  time: 'View',
  expenses: 'View',
  team: 'View',
  resourcing: 'View',
  milestones: 'View',
  changeRequests: 'View',
  audit: 'Hidden',
  reports: 'Hidden',
  forecast: 'Hidden',
} as const;

// ── Platform Roles ────────────────────────────────────────────────────────────

export const MOCK_ROLES: Role[] = [
  {
    id: 'role-administrator',
    name: 'Administrator',
    description: 'Full access to all features, settings, and user management across the platform.',
    type: 'Platform',
    status: 'Active',
    userCount: 3,
    permissions: {
      projectVisibility: 'all',
      projectActions: {
        create: true,
        edit: true,
        delete: true,
        changeStatus: true,
        assignKeyPeople: true,
      },
      projectTabs: ALL_PROJECT_TABS_EDIT,
      reports: {
        executiveDashboard: true,
        pmDashboard: true,
        projectTeamDashboard: true,
        plReport: true,
        wipReport: true,
        utilisationReport: true,
        budgetVarianceReport: true,
        auditLogReport: true,
        projectTransactionsReport: true,
        projectStatusReport: true,
        projectForecastReport: true,
        profitabilityReport: true,
        cashFlowForecastReport: true,
      },
      settings: {
        generalSettings: true,
        configureAuditTrail: true,
        projectSettings: true,
        integrations: true,
        rolesAndPermissions: true,
        templates: true,
        notifications: true,
      },
    },
  },
  {
    id: 'role-project-manager',
    name: 'Project Manager',
    description: 'Manages projects they are assigned to, with access to financial and resourcing data.',
    type: 'Platform',
    status: 'Active',
    userCount: 8,
    permissions: {
      projectVisibility: 'managed-allocated',
      projectActions: {
        create: true,
        edit: true,
        delete: false,
        changeStatus: true,
        assignKeyPeople: true,
      },
      projectTabs: PM_PROJECT_TABS,
      reports: {
        executiveDashboard: false,
        pmDashboard: true,
        projectTeamDashboard: false,
        plReport: true,
        wipReport: true,
        utilisationReport: true,
        budgetVarianceReport: true,
        auditLogReport: false,
        projectTransactionsReport: true,
        projectStatusReport: true,
        projectForecastReport: true,
        profitabilityReport: false,
        cashFlowForecastReport: true,
      },
      settings: {
        generalSettings: false,
        configureAuditTrail: false,
        projectSettings: false,
        integrations: false,
        rolesAndPermissions: false,
        templates: false,
        notifications: false,
      },
    },
  },
  {
    id: 'role-executive',
    name: 'Executive',
    description: 'Read-only access across all projects and financial reports for senior leadership.',
    type: 'Platform',
    status: 'Active',
    userCount: 4,
    permissions: {
      projectVisibility: 'all',
      projectActions: {
        create: false,
        edit: false,
        delete: false,
        changeStatus: false,
        assignKeyPeople: false,
      },
      projectTabs: EXECUTIVE_PROJECT_TABS,
      reports: {
        executiveDashboard: true,
        pmDashboard: false,
        projectTeamDashboard: false,
        plReport: true,
        wipReport: true,
        utilisationReport: true,
        budgetVarianceReport: true,
        auditLogReport: false,
        projectTransactionsReport: true,
        projectStatusReport: true,
        projectForecastReport: true,
        profitabilityReport: true,
        cashFlowForecastReport: true,
      },
      settings: {
        generalSettings: false,
        configureAuditTrail: false,
        projectSettings: false,
        integrations: false,
        rolesAndPermissions: false,
        templates: false,
        notifications: false,
      },
    },
  },
  {
    id: 'role-project-team',
    name: 'Project Team',
    description: 'View access to projects they are allocated to. No financial data visible.',
    type: 'Platform',
    status: 'Active',
    userCount: 22,
    permissions: {
      projectVisibility: 'managed-allocated',
      projectActions: {
        create: false,
        edit: false,
        delete: false,
        changeStatus: false,
        assignKeyPeople: false,
      },
      projectTabs: PROJECT_TEAM_PROJECT_TABS,
      reports: {
        executiveDashboard: false,
        pmDashboard: false,
        projectTeamDashboard: true,
        plReport: false,
        wipReport: false,
        utilisationReport: false,
        budgetVarianceReport: false,
        auditLogReport: false,
        projectTransactionsReport: false,
        projectStatusReport: false,
        projectForecastReport: false,
        profitabilityReport: false,
        cashFlowForecastReport: false,
      },
      settings: {
        generalSettings: false,
        configureAuditTrail: false,
        projectSettings: false,
        integrations: false,
        rolesAndPermissions: false,
        templates: false,
        notifications: false,
      },
    },
  },
];

// ── Mock users per role ───────────────────────────────────────────────────────

export const MOCK_ROLE_USERS: Record<string, RoleUser[]> = {
  'role-administrator': [
    { id: 'u1', name: 'Sarah Mitchell', email: 'sarah.mitchell@company.com', lastActive: '2025-01-15' },
    { id: 'u2', name: 'James Carter', email: 'james.carter@company.com', lastActive: '2025-01-14' },
    { id: 'u3', name: 'Emma Thompson', email: 'emma.thompson@company.com', lastActive: '2025-01-10' },
  ],
  'role-project-manager': [
    { id: 'u4', name: 'David Hargreaves', email: 'david.hargreaves@company.com', lastActive: '2025-01-15' },
    { id: 'u5', name: 'Priya Patel', email: 'priya.patel@company.com', lastActive: '2025-01-13' },
    { id: 'u6', name: 'Tom Bradley', email: 'tom.bradley@company.com', lastActive: '2025-01-12' },
    { id: 'u7', name: 'Claire Owens', email: 'claire.owens@company.com', lastActive: '2025-01-11' },
    { id: 'u8', name: 'Liam Forsyth', email: 'liam.forsyth@company.com', lastActive: '2025-01-09' },
    { id: 'u9', name: 'Natalie Webb', email: 'natalie.webb@company.com', lastActive: '2025-01-08' },
    { id: 'u10', name: 'George Simmons', email: 'george.simmons@company.com', lastActive: '2025-01-07' },
    { id: 'u11', name: 'Amelia Cross', email: 'amelia.cross@company.com', lastActive: null },
  ],
  'role-executive': [
    { id: 'u12', name: 'Richard Ashworth', email: 'richard.ashworth@company.com', lastActive: '2025-01-14' },
    { id: 'u13', name: 'Diana Thornton', email: 'diana.thornton@company.com', lastActive: '2025-01-12' },
    { id: 'u14', name: 'Mark Ellison', email: 'mark.ellison@company.com', lastActive: '2025-01-10' },
    { id: 'u15', name: 'Fiona Gallagher', email: 'fiona.gallagher@company.com', lastActive: '2025-01-05' },
  ],
  'role-project-team': [
    { id: 'u16', name: 'Oliver Grant', email: 'oliver.grant@company.com', lastActive: '2025-01-15' },
    { id: 'u17', name: 'Sophie Clarke', email: 'sophie.clarke@company.com', lastActive: '2025-01-14' },
    { id: 'u18', name: 'Harry Stone', email: 'harry.stone@company.com', lastActive: '2025-01-13' },
    { id: 'u19', name: 'Mia Jenkins', email: 'mia.jenkins@company.com', lastActive: '2025-01-12' },
    { id: 'u20', name: 'Ethan Pearce', email: 'ethan.pearce@company.com', lastActive: '2025-01-11' },
    { id: 'u21', name: 'Isabella Ward', email: 'isabella.ward@company.com', lastActive: null },
    { id: 'u22', name: 'Lucas Hunt', email: 'lucas.hunt@company.com', lastActive: '2025-01-09' },
  ],
};

// ── Mock employees (all staff — used to populate the Add Users dialog in demo mode) ──

export const MOCK_EMPLOYEES: Array<{ id: number; name: string; email: string }> = [
  // Assigned users (already in roles above)
  { id: 1,  name: 'Sarah Mitchell',   email: 'sarah.mitchell@company.com' },
  { id: 2,  name: 'James Carter',     email: 'james.carter@company.com' },
  { id: 3,  name: 'Emma Thompson',    email: 'emma.thompson@company.com' },
  { id: 4,  name: 'David Hargreaves', email: 'david.hargreaves@company.com' },
  { id: 5,  name: 'Priya Patel',      email: 'priya.patel@company.com' },
  { id: 6,  name: 'Tom Bradley',      email: 'tom.bradley@company.com' },
  { id: 7,  name: 'Claire Owens',     email: 'claire.owens@company.com' },
  { id: 8,  name: 'Liam Forsyth',     email: 'liam.forsyth@company.com' },
  { id: 9,  name: 'Natalie Webb',     email: 'natalie.webb@company.com' },
  { id: 10, name: 'George Simmons',   email: 'george.simmons@company.com' },
  { id: 11, name: 'Amelia Cross',     email: 'amelia.cross@company.com' },
  { id: 12, name: 'Richard Ashworth', email: 'richard.ashworth@company.com' },
  { id: 13, name: 'Diana Thornton',   email: 'diana.thornton@company.com' },
  { id: 14, name: 'Mark Ellison',     email: 'mark.ellison@company.com' },
  { id: 15, name: 'Fiona Gallagher',  email: 'fiona.gallagher@company.com' },
  { id: 16, name: 'Oliver Grant',     email: 'oliver.grant@company.com' },
  { id: 17, name: 'Sophie Clarke',    email: 'sophie.clarke@company.com' },
  { id: 18, name: 'Harry Stone',      email: 'harry.stone@company.com' },
  { id: 19, name: 'Mia Jenkins',      email: 'mia.jenkins@company.com' },
  { id: 20, name: 'Ethan Pearce',     email: 'ethan.pearce@company.com' },
  { id: 21, name: 'Isabella Ward',    email: 'isabella.ward@company.com' },
  { id: 22, name: 'Lucas Hunt',       email: 'lucas.hunt@company.com' },
  // Unassigned employees (not yet in any role)
  { id: 23, name: 'Alice Freeman',    email: 'alice.freeman@company.com' },
  { id: 24, name: 'Ben Whitfield',    email: 'ben.whitfield@company.com' },
  { id: 25, name: 'Caroline Moss',    email: 'caroline.moss@company.com' },
  { id: 26, name: 'Daniel Hurst',     email: 'daniel.hurst@company.com' },
  { id: 27, name: 'Eleanor Vickers',  email: 'eleanor.vickers@company.com' },
];

// ── Helper: compute total permissions enabled for a role ──────────────────────

export function countEnabledPermissions(role: Role): number {
  const p = role.permissions;
  let count = 0;

  // Project actions
  count += Object.values(p.projectActions).filter(Boolean).length;

  // Project tabs (Edit = 2 pts, View = 1 pt, Hidden = 0)
  count += Object.values(p.projectTabs).filter((v) => v !== 'Hidden').length;

  // Reports
  count += Object.values(p.reports).filter(Boolean).length;

  // Settings
  count += Object.values(p.settings).filter(Boolean).length;

  return count;
}

// ── Helper: generate a new ID for custom roles ────────────────────────────────

export function generateRoleId(): string {
  return `role-custom-${Date.now()}`;
}
